import React, {
  Fragment, useEffect, useRef, useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import Form, { ChangedField } from 'shared/form';
import {
  TargetModel, TargetModelAccessFunction, TargetModelAccessFunctions, TargetModelAfCCC, TargetModelAfCCCSettings,
} from 'data/target/target.types';
import { CaseModel } from 'data/case/case.types';
import {
  FormTemplate, FormTemplateField, FormTemplateOption, FormTemplateSection,
} from 'data/template/template.types';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import './ribbon-c20-csc-target-ccc.scss';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/accessFunction/accessFunction.actions';
import { KeyValuePair } from 'data/types';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import { getTargetDeliveryTemplateAccessFunctionList } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { TargetDeliveryTemplateAccessFunction } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { getTemplateOptions } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import { TargetInfoHashItem } from '../target-info-defs';
import { getTargetTemplateValues } from './utils';
import ribbonC20CSCTemplate from './RibbonC20CSCTargetCCCTemplate';

export interface GeneralRibbonC20CSC {
  targetDeliveryTemplate: string
}

export interface USNBDTargetInformation {
  agencyid?: string
  cdcid?: string
  assoc?: string
}

export interface USNBDCallContentChannelInformation {
  definition?: string
  ccraccess?: string
  dnsrc?: string
  dndest?: string
  ccrindex?: string
  status?: string
  cccassoc?: string
  result?: string
}

const mapState = (state: RootState) => ({
  targetTemplates: selectors.getTargetDeliveryTemplateAccessFunctionList(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state)
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
  getTargetDeliveryTemplateAccessFunctionList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type RibbonC20CSCTargetCCCProps = PropsFromRedux & {
  handleUpdate: (afSettings: TargetModelAfCCCSettings[]) => TargetModel[];
  handleTargetInfoHashItemUpdate: (targetInfoHashItem: TargetInfoHashItem) => TargetModel[];
  handleSubmit: (targets: TargetModel[]) => void;
  targetInfoHashItem: TargetInfoHashItem | null;
  afDetails: TargetModelAccessFunction;
  afid: string;
  currentStateMode: any;
  targetId: string;
  caseId: string;
  targetType: string;
  selectedAfs: { afs: TargetModelAccessFunctions }
  afDIDetails: any;
  warrantCase: CaseModel;
  selectedTargetAf: any;
}

function getAfCCCInfo(afccc: TargetModelAfCCC[], afDetailsType?: string): TargetModelAfCCC | undefined {
  return afccc?.find((af: any) => af.afType === afDetailsType);
}

function adaptTargetCCCForm(
  warrantCase: CaseModel,
  accessFunction: AccessFunction | null,
  targetTemplates: TargetDeliveryTemplateAccessFunction[],
  afccc?: TargetModelAfCCC[],
  afDetailsType?: string,
  afId?: string,
  currentStateMode?: CONFIG_FORM_MODE,
  selectedAfs?: { afs: TargetModelAccessFunctions, actualPrimaryType?: string },
  afid?: string,
  targetId?: string,
): FormTemplate {
  const clonedTemplate = cloneDeep(ribbonC20CSCTemplate);
  const templateOptions = getTemplateOptions(targetTemplates || [], afDetailsType || '');

  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [
    { value: '', label: 'Select a template' },
    ...templateOptions,
  ];

  if (
    warrantCase == null ||
    afccc == null ||
    afDetailsType == null ||
    afId == null ||
    currentStateMode == null ||
    selectedAfs == null
  ) {

    return clonedTemplate;
  }

  const afCCCInfo = getAfCCCInfo(afccc, afDetailsType);

  if (afCCCInfo == null) {
    return clonedTemplate;
  }

  const cccOptions = afCCCInfo.afSettings.find((setting) => setting.afids[0] === afId)?.options
    .reduce((total, option) => ({
      ...total,
      [option.key]: option.value,
    }), {}) as { [key: string]: string | number | boolean | string[] } | undefined;

  if (cccOptions == null) {
    return clonedTemplate;
  }

  Object.keys((clonedTemplate.general as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.general as FormTemplateSection).fields[key] != null) {
      if (cccOptions?.[key] != null) {
        (clonedTemplate.general as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      }
      (clonedTemplate.general as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  Object.keys((clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields[key] != null) {
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  Object.keys((clonedTemplate.usnbdTargetInformation as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.usnbdTargetInformation as FormTemplateSection).fields[key] != null) {
      (clonedTemplate.usnbdTargetInformation as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      (clonedTemplate.usnbdTargetInformation as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  switch (cccOptions?.definition) {
    case 'PAIRED':
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
      break;
    default:
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined DN';
      (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
      break;
  }

  updateTemplateFieldsValues(currentStateMode, clonedTemplate, warrantCase, targetId as string);
  clonedTemplate.formId = Date.now().toString();
  return clonedTemplate;
}

function enableField(
  field: FormTemplateField,
  shouldEnable: boolean,
): void {

  if (shouldEnable) {
    field.disabled = false;
    field.hide = 'false';
  }
}

function disableField(
  field: FormTemplateField,
  shouldDisable: boolean,
): void {

  if (shouldDisable) {
    field.disabled = true;
  }
}

function updateTemplateFieldsValues(currentStateMode: CONFIG_FORM_MODE, template: FormTemplate, warrantCase: CaseModel, targetId: string) {
  const targetType = warrantCase.targets.find((target) => target.id === targetId)?.actualPrimaryType;

  if (currentStateMode === CONFIG_FORM_MODE.View) {
    enableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.ccrindex, true);
    enableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.status, true);
    enableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.cccassoc, true);
    enableField((template.usnbdTargetInformation as FormTemplateSection).fields.assoc, true);
  } else if (currentStateMode === CONFIG_FORM_MODE.Edit) {
    disableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.definition, true);
    disableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.ccraccess, true);
    disableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc, true);
    disableField((template.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest, true);
    disableField((template.usnbdTargetInformation as FormTemplateSection).fields.agencyid, true);
    disableField((template.usnbdTargetInformation as FormTemplateSection).fields.cdcid, true);
    disableField((template.general as FormTemplateSection).fields.targetDeliveryTemplate, true);
  }
}

function getLeaOptions(accessFunction: AccessFunction) {
  let leaOptions: FormTemplateOption[] = [];
  if (accessFunction) {
    const leas: string[] = (accessFunction.dataInterfaces ?? [])
      .flatMap((df) => df.options)
      .filter((o) => ['lea', 'leacs', 'leaps'].includes(o.key))
      .map((o) => o.value as string);

    leaOptions = [
      ...leas.map((lea) => {
        return {
          label: lea,
          value: lea
        };
      })
    ];
  }
  return leaOptions;
}

const RibbonC20CSCTargetCCC = ({
  afDetails,
  handleUpdate,
  afid,
  currentStateMode,
  targetId,
  selectedAfs,
  warrantCase,
  selectedTargetAf,
  deliveryFunctionId,
  accessFunction,
  targetTemplates,
  getAccessFunctionWithViewOptions,
}: RibbonC20CSCTargetCCCProps) => {
  const [template, setTemplate] = useState<FormTemplate>(ribbonC20CSCTemplate);
  const usnbdTargetInformationRef = useRef<Form>(null);
  const usnbdCallContentChannelInformationRef = useRef<Form>(null);
  const generalRef = useRef<Form>(null);
  const { t } = useTranslation();

  const targetAccessFunctionId = selectedTargetAf.id;

  useEffect(() => {
    if (targetAccessFunctionId) {
      getAccessFunctionWithViewOptions(deliveryFunctionId, targetAccessFunctionId);
    }
  }, [targetAccessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    const clonedTemplate = cloneDeep(template);

    const templateOptions = getTemplateOptions(targetTemplates || [], afDetails.type || '');

    (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [
      { value: '', label: 'Select a template' },
      ...templateOptions,
    ];
    (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = '';

    clonedTemplate.key = Date.now().toString();
    setTemplate(clonedTemplate);
  }, [selectedTargetAf, targetTemplates]);

  function fieldUpdate({ name, value }: ChangedField) {
    const clonedTemplate = cloneDeep(template);

    let usnbdTargetInformationFields: USNBDTargetInformation = usnbdTargetInformationRef?.current?.getValues();
    let usnbdCallContentChannelInformationFields: USNBDCallContentChannelInformation = usnbdCallContentChannelInformationRef?.current?.getValues();

    if (name === 'targetDeliveryTemplate') {
      const clonedTargetTemplate = cloneDeep(targetTemplates.find((targetTemplate) => String(targetTemplate.id) === value));

      usnbdTargetInformationFields = getTargetTemplateValues('usnbdTargetInformation', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);
      usnbdCallContentChannelInformationFields = getTargetTemplateValues('usnbdCallContentChannelInformation', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);

      (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = value;
      if (!usnbdCallContentChannelInformationFields) {
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.definition.initial = 'PAIRED';
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
      }
    } else if (name === 'definition') {
      if (value === 'PAIRED') {
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
      } else if (value === 'COMBINED') {
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
        (clonedTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined\'s DN';
      }
    }

    const generalFields: GeneralRibbonC20CSC = generalRef?.current?.getValues();

    const isValid = generalRef?.current?.isValid(true, true)
      && usnbdTargetInformationRef?.current?.isValid(true, true)
      && usnbdCallContentChannelInformationRef?.current?.isValid(true, true);

    const usnbdOptions: KeyValuePair[] = [
      ...Object.entries(usnbdTargetInformationFields ?? {}).map(([key, optionValue]) => ({
        key,
        value: optionValue == null ? '' : String(optionValue),
      })),
      ...Object.entries(usnbdCallContentChannelInformationFields ?? {}).map(([key, optionValue]) => ({
        key,
        value: optionValue == null ? '' : String(optionValue),
      })),
    ];

    let options = [
      ...usnbdOptions,
    ];

    options = [...options, { key: 'targetDeliveryTemplate', value: generalFields.targetDeliveryTemplate }];
    const afCCCSettings: TargetModelAfCCCSettings[] = [{
      afids: [afDetails.id as string],
      isValid,
      options,
      autowired: false,
    }];

    handleUpdate(afCCCSettings);

    // Regenerate form key only when template selection changes; doing this on every keystroke
    // remounts forms and resets text fields to their initial values.
    if (name === 'targetDeliveryTemplate' || name === 'definition') {
      clonedTemplate.key = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }

  return (
    <Fragment>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).general as FormTemplateSection).fields
        }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        ).general as FormTemplateSection).metadata?.layout?.rows}
        ref={generalRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      {/* USNBD Target Information */}
      <div className="subsection-header">
        {t('warrant.target.form.ribbonC20CSC.usnbdTargetInformation')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).usnbdTargetInformation as FormTemplateSection).fields
        }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.usnbdTargetInformation as FormTemplateSection).metadata?.layout?.rows}
        ref={usnbdTargetInformationRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      {/* USNBD Call Content Channel Information */}
      <div className="subsection-header">
        {t('warrant.target.form.ribbonC20CSC.usnbdCallContentChannelInformation')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).usnbdCallContentChannelInformation as FormTemplateSection).fields
        }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.usnbdCallContentChannelInformation as FormTemplateSection).metadata?.layout?.rows}
        ref={usnbdCallContentChannelInformationRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
    </Fragment>
  );
};

export default connector(RibbonC20CSCTargetCCC);
